import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, CarFront, TrendingUp, TrendingDown, PackagePlus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function VehicleDealer() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [sellTarget, setSellTarget] = useState(null);
  const [restockTarget, setRestockTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", category: "", purchase_cost: "", sale_price: "" });
  const [customer, setCustomer] = useState("");
  const [qty, setQty] = useState(1);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(false);

  const loadVehicles = () => api.get("/vehicles").then((r) => setVehicles(r.data));
  const loadMoney = () => {
    api.get("/shop/balance").then((r) => setBalance(r.data));
    api.get("/shop/ledger").then((r) => setLedger(r.data));
  };

  useEffect(() => {
    loadVehicles();
    loadMoney();
    const t = setInterval(loadMoney, 5000);
    return () => clearInterval(t);
  }, []);

  const categories = useMemo(() => ["All", ...new Set(vehicles.map((v) => v.category).filter(Boolean))], [vehicles]);
  const filtered = vehicles.filter(
    (v) =>
      (category === "All" || v.category === category) &&
      v.name.toLowerCase().includes(query.toLowerCase())
  );

  const sell = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/sell", { vehicle_id: sellTarget.id, qty: Number(qty), customer: customer.trim() });
      toast.success(`Sold ${sellTarget.name} ×${qty} — ${fmt(data.total)} into shop money`);
      setSellTarget(null); setCustomer(""); setQty(1);
      loadVehicles(); loadMoney();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const restock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/restock", { vehicle_id: restockTarget.id, qty: Number(qty) });
      toast.success(`Bought ${qty} × ${restockTarget.name} — ${fmt(data.cost)} out of shop money`);
      setRestockTarget(null); setQty(1);
      loadVehicles(); loadMoney();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const addVehicle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vehicles", {
        name: addForm.name.trim(),
        category: (addForm.category || "other").trim().toLowerCase(),
        purchase_cost: Number(addForm.purchase_cost),
        sale_price: Number(addForm.sale_price),
      });
      toast.success(`${addForm.name} added to the list — buy stock to put it on the lot`);
      setAddOpen(false);
      setAddForm({ name: "", category: "", purchase_cost: "", sale_price: "" });
      loadVehicles();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const removeVehicle = async (v) => {
    if (!window.confirm(`Remove "${v.name}" (${fmt(v.sale_price)}) from the list?`)) return;
    await api.delete(`/vehicles/${v.id}`);
    toast.success("Vehicle removed");
    loadVehicles();
  };

  const resync = async () => {
    if (!window.confirm("Reload the full vehicle list from the master file? Stock counts are kept by name.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/resync");
      toast.success(`List resynced — ${data.count} vehicles`);
      loadVehicles();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const saveVehicle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/vehicles/${editTarget.id}`, {
        name: editForm.name,
        purchase_cost: Number(editForm.purchase_cost),
        sale_price: Number(editForm.sale_price),
      });
      toast.success("Vehicle updated");
      setEditTarget(null);
      loadVehicles();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="dealer-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
            <span className="h-px w-8 bg-primary/60" />
            VEHICLE DEALER <span className="font-jp text-muted-foreground">車両販売</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">The Lot</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button
              data-testid="add-vehicle-btn"
              onClick={() => setAddOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-widest rounded-sm h-11 px-5"
            >
              <CarFront size={14} className="mr-2" /> ADD VEHICLE
            </Button>
            <Button
              data-testid="resync-vehicles-btn"
              onClick={resync}
              disabled={busy}
              variant="outline"
              className="border-primary/40 text-primary hover:bg-primary/10 font-display text-[11px] tracking-widest rounded-sm h-11 px-5"
            >
              <RefreshCw size={14} className="mr-2" /> RESYNC LIST
            </Button>
          </div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid sm:grid-cols-3 gap-4 mb-6"
        data-testid="money-strip"
      >
        <div className="bg-card border border-primary/30 rounded-md p-5 shadow-[0_0_30px_rgba(0,255,102,0.07)]">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.25em] text-muted-foreground">SHOP MONEY</span>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
          </div>
          <div className={`font-display text-3xl font-black mt-2 ${balance && balance.balance < 0 ? "text-destructive" : "text-primary neon-glow"}`} data-testid="shop-balance">
            {balance ? fmt(balance.balance) : "—"}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5">live — updates as sales & purchases happen</div>
        </div>
        <div className="bg-card border border-border rounded-md p-5">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground flex items-center gap-1.5"><TrendingUp size={12} className="text-emerald-400" /> MONEY IN</span>
          <div className="font-display text-2xl font-black text-emerald-400 mt-2" data-testid="money-in">{balance ? fmt(balance.money_in) : "—"}</div>
        </div>
        <div className="bg-card border border-border rounded-md p-5">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground flex items-center gap-1.5"><TrendingDown size={12} className="text-amber-400" /> MONEY OUT</span>
          <div className="font-display text-2xl font-black text-amber-400 mt-2" data-testid="money-out">{balance ? fmt(balance.money_out) : "—"}</div>
        </div>
      </motion.div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="dealer-search"
            placeholder={`Search ${vehicles.length} vehicles…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-card border-border rounded-sm h-10 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="dealer-categories">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`dealer-cat-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`text-[10px] tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                category === c ? "bg-primary text-primary-foreground border-primary font-bold" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
              }`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="dealer-table">
        <div className="grid grid-cols-12 text-[10px] tracking-[0.2em] text-muted-foreground border-b border-border px-4 py-3">
          <span className="col-span-4">VEHICLE</span>
          <span className="hidden sm:block col-span-2">CATEGORY</span>
          <span className="col-span-2 text-right">STOCK</span>
          <span className="hidden sm:block col-span-1 text-right">COST</span>
          <span className="col-span-3 sm:col-span-2 text-right">SALE PRICE</span>
          <span className="col-span-3 sm:col-span-1 text-right">ACTIONS</span>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {filtered.map((v) => (
            <div key={v.id} className="grid grid-cols-12 items-center px-4 py-3 text-xs border-b border-border/50 last:border-0 hover:bg-secondary/30 transition-colors" data-testid={`dealer-row-${v.id}`}>
              <span className="col-span-4 text-foreground font-semibold flex items-center gap-2 min-w-0">
                <CarFront size={13} className="text-primary shrink-0" />
                <span className="truncate">{v.name}</span>
              </span>
              <span className="hidden sm:block col-span-2">
                <span className="text-[9px] tracking-widest text-primary/70 border border-primary/20 bg-primary/5 px-1.5 py-0.5 rounded-sm">{(v.category || "—").toUpperCase()}</span>
              </span>
              <span className={`col-span-2 text-right font-semibold ${v.stock > 0 ? "text-foreground" : "text-destructive"}`} data-testid={`dealer-stock-${v.id}`}>
                {v.stock > 0 ? v.stock : "OUT"}
              </span>
              <span className="hidden sm:block col-span-1 text-right text-muted-foreground">{fmt(v.purchase_cost)}</span>
              <span className="col-span-3 sm:col-span-2 text-right font-display font-bold text-foreground">{fmt(v.sale_price)}</span>
              <span className="col-span-3 sm:col-span-1 flex justify-end gap-1">
                <button
                  data-testid={`dealer-sell-${v.id}`}
                  onClick={() => { setSellTarget(v); setQty(1); setCustomer(""); }}
                  className="text-[9px] tracking-widest px-2 py-1.5 rounded-sm bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
                >
                  SELL
                </button>
                {isAdmin && (
                  <>
                    <button
                      data-testid={`dealer-restock-${v.id}`}
                      onClick={() => { setRestockTarget(v); setQty(1); }}
                      title="Purchase stock"
                      className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <PackagePlus size={13} />
                    </button>
                    <button
                      data-testid={`dealer-edit-${v.id}`}
                      onClick={() => { setEditTarget(v); setEditForm({ name: v.name, purchase_cost: v.purchase_cost, sale_price: v.sale_price }); }}
                      className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      data-testid={`dealer-delete-${v.id}`}
                      onClick={() => removeVehicle(v)}
                      className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
          {!filtered.length && (
            <div className="text-center text-muted-foreground text-xs py-14" data-testid="dealer-empty">No vehicles match.</div>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-5 mt-4" data-testid="activity-feed">
        <span className="text-[10px] tracking-[0.25em] text-muted-foreground">MONEY ACTIVITY</span>
        <div className="mt-3 space-y-2 max-h-[220px] overflow-y-auto pr-1">
          {ledger.slice(0, 15).map((e) => (
            <div key={e.id} className="flex items-center justify-between text-xs border-b border-border/40 pb-2" data-testid={`ledger-${e.id}`}>
              <span className="text-muted-foreground truncate mr-3">
                <span className="text-foreground">{e.label}</span>
                <span className="ml-2 text-[10px]">{new Date(e.created_at).toLocaleString()}</span>
              </span>
              <span className={`font-display font-bold shrink-0 ${e.amount >= 0 ? "text-emerald-400" : "text-amber-400"}`}>
                {e.amount >= 0 ? "+" : "−"}{fmt(Math.abs(e.amount))}
              </span>
            </div>
          ))}
          {!ledger.length && <div className="text-muted-foreground text-xs py-6 text-center" data-testid="ledger-empty">No money movement yet.</div>}
        </div>
      </div>

      <Dialog open={!!sellTarget} onOpenChange={() => setSellTarget(null)}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="sell-vehicle-modal">
          {sellTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-wide">SELL — {sellTarget.name}</DialogTitle>
              </DialogHeader>
              <form onSubmit={sell} className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">CUSTOMER (OPTIONAL)</label>
                  <Input data-testid="sell-customer-input" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Buyer name / Citizen ID" className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">QTY</label>
                  <Input data-testid="sell-qty-input" type="number" min="1" required value={qty} onChange={(e) => setQty(e.target.value)} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                </div>
                <div className="flex justify-between items-center bg-primary/10 border border-primary/30 rounded-sm px-3 py-2.5">
                  <span className="text-[10px] tracking-[0.2em] text-primary">INTO SHOP MONEY</span>
                  <span className="font-display text-lg font-black text-primary" data-testid="sell-total">+{fmt(sellTarget.sale_price * (Number(qty) || 0))}</span>
                </div>
                <Button data-testid="sell-confirm-btn" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
                  {busy ? "SELLING…" : "CONFIRM SALE"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!restockTarget} onOpenChange={() => setRestockTarget(null)}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="restock-modal">
          {restockTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-wide">BUY STOCK — {restockTarget.name}</DialogTitle>
              </DialogHeader>
              <form onSubmit={restock} className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">QTY TO PURCHASE</label>
                  <Input data-testid="restock-qty-input" type="number" min="1" required value={qty} onChange={(e) => setQty(e.target.value)} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                </div>
                <div className="flex justify-between items-center bg-amber-400/10 border border-amber-400/30 rounded-sm px-3 py-2.5">
                  <span className="text-[10px] tracking-[0.2em] text-amber-400">OUT OF SHOP MONEY</span>
                  <span className="font-display text-lg font-black text-amber-400" data-testid="restock-cost">−{fmt(restockTarget.purchase_cost * (Number(qty) || 0))}</span>
                </div>
                <Button data-testid="restock-confirm-btn" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
                  {busy ? "PURCHASING…" : "CONFIRM PURCHASE"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="edit-vehicle-modal">
          {editTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-wide">EDIT VEHICLE</DialogTitle>
              </DialogHeader>
              <form onSubmit={saveVehicle} className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">NAME</label>
                  <Input data-testid="edit-vehicle-name" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">PURCHASE COST $</label>
                    <Input data-testid="edit-vehicle-cost" type="number" min="0" step="1" required value={editForm.purchase_cost} onChange={(e) => setEditForm({ ...editForm, purchase_cost: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">SALE PRICE $</label>
                    <Input data-testid="edit-vehicle-price" type="number" min="0" step="1" required value={editForm.sale_price} onChange={(e) => setEditForm({ ...editForm, sale_price: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                  </div>
                </div>
                <Button data-testid="edit-vehicle-save" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
                  {busy ? "SAVING…" : "SAVE VEHICLE"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="add-vehicle-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">ADD VEHICLE</DialogTitle>
          </DialogHeader>
          <form onSubmit={addVehicle} className="space-y-4" data-testid="add-vehicle-form">
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">VEHICLE NAME</label>
              <Input data-testid="add-vehicle-name" required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="e.g. Annis Elegy RH8 Custom" className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">CATEGORY</label>
              <Input data-testid="add-vehicle-category" value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} placeholder="e.g. sports" className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">PURCHASE COST $</label>
                <Input data-testid="add-vehicle-cost" type="number" min="0" step="1" required value={addForm.purchase_cost} onChange={(e) => setAddForm({ ...addForm, purchase_cost: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">SALE PRICE $</label>
                <Input data-testid="add-vehicle-price" type="number" min="0" step="1" required value={addForm.sale_price} onChange={(e) => setAddForm({ ...addForm, sale_price: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              New vehicles start with 0 stock — buy stock with the package icon to put them on the lot.
            </p>
            <Button data-testid="add-vehicle-save" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
              {busy ? "ADDING…" : "ADD TO LIST"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
